// The shooting-feel director: one place that turns shots, hits, kills and blasts into lens
// punch, hit-stop, hit markers, robot reactions, death choice, rumble, adaptive triggers and the
// sound call sites. Everything here is per-peer presentation: it never touches the sim, so it
// can't break prediction or multiplayer (no RPCs, no shared state, no randomness that matters).
//
// Wiring (client/game.ts): onEvent() sees every server event first; shot() runs on your own
// predicted trigger pull; update() runs each frame after the session has delivered events and
// before the robots draw (it resolves deaths then); hud() fills the hit-marker fields.

import * as THREE from 'three';
import { Ev, GameEvent } from '../../sim/codec';
import { ENEMIES, EType } from '../../sim/defs';
import { OKind } from '../../sim/arsenal';
import { Proj, WEAPONS, WeaponDef } from '../../sim/weapons';
import type { NetEnemy } from '../../sim/protocol';
import type { CameraRig } from '../camera';
import type { Input } from '../input/input';
import type { Sfx } from '../audio/sfx';
import { BANK } from '../audio/sfx';
import type { Fx } from '../render/fx/index';
import type { EnemyRS, HudState } from '../render/contracts';
import { DeathKind } from '../render/contracts';
import { DualSense, TRIG } from './dualsense';
import { FeelFx } from './feelfx';
import { L2_AIM, Shell, feelOf } from './recoil';

const DEG = Math.PI / 180;
/** kills inside this window stack into a multikill confirm */
const STREAK = 1.1;
/** robot hit info lives this long (direction + power for the flinch) */
const HIT_KEEP = 1.0;

interface HitInfo { dx: number; dz: number; pow: number; frac: number; at: number; flags: number; dmgWin: number; winAt: number; blast: number; wid: number; head: boolean }
interface Blast { x: number; y: number; z: number; r: number; at: number }
interface Kill { id: number; type: number; x: number; y: number; z: number; killer: number }
interface Shock { x: number; y: number; z: number; at: number; r: number; k: number }

type SfxPlus = Sfx & { dip?: (amount: number, ms: number) => void; thump?: (strength: number) => void };

/** A shot fired from rest leaves the gun's socket before the pose has swung up, so the flash and
 *  tracer would start at the hip. If the socket hangs below the shoulder line, start the shot where the
 *  raised gun will be: shoulder height, a little right of and ahead of the body along the aim. */
export function raisedMuzzle(out: THREE.Vector3, px: number, py: number, pz: number, dx: number, dy: number, dz: number) {
  if (out.y >= py + 1.2) return out;
  const n = Math.hypot(dx, dy, dz) || 1; dx /= n; dy /= n; dz /= n;
  const l = Math.hypot(dx, dz) || 1, fx = dx / l, fz = dz / l;
  return out.set(px - fz * 0.3 + dx * 0.6, py + 1.42 + dy * 0.6, pz + fx * 0.3 + dz * 0.6);
}

export class Feel {
  readonly fxx: FeelFx;
  readonly pad = new DualSense();
  private time = 0;
  get now() { return this.time; }
  /** hit-stop: FX clock rate (1 normal, ~0.05 frozen) and when it ends */
  private stopUntil = 0;
  private stopDepth = 0;
  private dirtAt = -1;
  // hud
  private hitKind = 0; private hitPow = 0; private killHead = false; private killBig = false; private killN = 0; private lastKillAt = -9;
  // per robot
  private hits = new Map<number, HitInfo>();
  private blasts: Blast[] = [];
  private kills: Kill[] = [];
  /** live shockwaves for the post pass */
  readonly shocks: Shock[] = [];
  private later: { at: number; name: string; x: number; y: number; z: number; vol: number; pitch: number }[] = [];
  private whizzed = new Set<number>();
  private shotIdx = 0;
  private firingUntil = 0;
  private v = new THREE.Vector3();
  private _shake = 1;
  /** screen-shake scale from settings (0 off .. 1): lens punch here, trauma shake in the rig */
  get shakeScale() { return this._shake; }
  set shakeScale(v: number) { this._shake = v; this.rig.shakeScale = v; }

  constructor(
    private rig: CameraRig, private input: Input, private sfx: SfxPlus, private fx: Fx,
    private robots: { kill?(id: number, kind: DeathKind, dx: number, dz: number, pow: number): void } | null,
    quality: number,
    /** where a player is (id → xyz), for hit directions */
    private playerPos: (id: number, out: THREE.Vector3) => THREE.Vector3 | null,
    private myId: () => number,
  ) {
    this.fxx = new FeelFx(fx, quality);
    // the pad driver takes over rumble once it's open (Gamepad API rumble and HID output fight)
    const orig = input.rumble.bind(input);
    input.rumble = (strong: number, weak: number, ms: number) => {
      if (input.paused) return;
      if (this.pad.on && input.glyphs === 'pad') { this.pad.rumble(strong, weak, ms); return; }
      orig(strong, weak, ms);
    };
    void this.pad.reconnect();
  }

  // ---------------------------------------------------------------- your own trigger pull
  /** your predicted shot: lens punch, rumble, trigger, casings. from/dir = muzzle + aim. */
  shot(w: WeaponDef, from: THREE.Vector3, dir: THREE.Vector3, aiming: boolean) {
    const f = feelOf(w.id);
    this.shotIdx++;
    const ads = aiming ? 0.6 : 1; // braced in the sights: a tighter kick
    const s = this.shakeScale;
    const yaw = (Math.random() * 2 - 1) * f.yaw * DEG * s;
    const roll = (Math.random() * 2 - 1) * f.roll * DEG * s;
    this.rig.punch(f.pitch * DEG * ads * s, yaw, roll, f.fov * ads * s, f.w);
    if (f.shake > 0) this.rig.shake(f.shake);
    this.input.rumble(f.rumble[0], f.rumble[1], f.rumble[2]);
    if (f.r2Fire) { this.firingUntil = this.time + w.interval / 60 + 0.12; }
    if (w.proj === Proj.Rocket || w.proj === Proj.Harpoon) this.fxx.launch(w.proj === Proj.Rocket ? 1 : 0.5, from.x, from.y, from.z, dir.x, dir.y, dir.z, true);
    if (f.shell !== Shell.None) {
      this.fxx.casing(f.shell, from.x, from.y, from.z, dir.x, dir.z);
      this.later.push({ at: this.time + 0.45 + Math.random() * 0.2, name: 'shell_tink', x: from.x, y: from.y - 1.2, z: from.z, vol: 0.35, pitch: 0.9 + Math.random() * 0.25 });
    }
  }

  /** muzzle / tracer extras for any cowboy's hitscan shot (own predicted or a teammate's) */
  shotFx(wid: number, from: THREE.Vector3, to: THREE.Vector3, dir: THREE.Vector3, local: boolean, pellet: number) {
    if (pellet === 0) this.fxx.muzzle(wid, from.x, from.y, from.z, dir.x, dir.y, dir.z, local);
    this.fxx.tracer(wid, from.x, from.y, from.z, to.x, to.y, to.z, local, local ? this.shotIdx : pellet);
  }

  /** your predicted impact: directional spray, heavier for big rounds */
  impactFx(w: WeaponDef, kind: number, to: THREE.Vector3, dir: THREE.Vector3, nx: number, ny: number, nz: number) {
    const heavy = Math.min(2, w.damage / 30);
    if (kind >= 2) { nx = -dir.x; ny = -dir.y; nz = -dir.z; }
    this.fxx.impact(kind, to.x, to.y, to.z, dir.x, dir.y, dir.z, nx, ny, nz, heavy);
    // world hits get a dirt/stone tick (once per pull: a shotgun's pellets would machine-gun it)
    if (kind === 1 && this.time - this.dirtAt > 0.05) { this.dirtAt = this.time; this.play('impact_dirt', to, 0.4, 0.85 + Math.random() * 0.3); }
  }

  /** a teammate's reload of a shell gun dumps hulls (yours too) */
  reload(wid: number, x: number, y: number, z: number, dx: number, dz: number) {
    const w = WEAPONS[wid];
    if (w && w.pellets > 1) this.fxx.shellsOut(1, x, y + 1.2, z, dx, dz);
  }

  // ---------------------------------------------------------------- server events
  onEvent(e: GameEvent, enemyPos: (id: number) => NetEnemy | null) {
    const f = e.f;
    const me = this.myId();
    switch (e.t) {
      case Ev.EnemyHit: {
        const [eid, dmg, flags, by, x, y, z] = f;
        const en = enemyPos(eid);
        const type = en ? en.type : 0;
        const hull = Math.max(20, ENEMIES[type]?.hp ?? 60) + (ENEMIES[type]?.shield ?? 0);
        let h = this.hits.get(eid);
        if (!h) { h = { dx: 0, dz: 0, pow: 0, frac: 0, at: -9, flags: 0, dmgWin: 0, winAt: -9, blast: 0, wid: -1, head: false }; this.hits.set(eid, h); }
        // direction: from the blast if one just went off around it, else from the shooter
        let dx = 0, dz = 0, blast = 0;
        const b = this.nearBlast(x, y, z);
        if (b) { dx = x - b.x; dz = z - b.z; blast = 1; }
        else if (by !== 255) { const p = this.playerPos(by, this.v); if (p) { dx = x - p.x; dz = z - p.z; } }
        if (flags & 8) blast = 1;
        const l = Math.hypot(dx, dz);
        if (l > 1e-3) { h.dx = dx / l; h.dz = dz / l; }
        // a burst of pellets in one tick adds up into one big hit
        if (this.time - h.winAt > 0.06) { h.dmgWin = 0; h.winAt = this.time; }
        h.dmgWin += dmg;
        h.frac = h.dmgWin / hull;
        h.pow = Math.min(2, h.frac * 4) * (flags & 4 ? 0.35 : 1);
        h.at = this.time; h.flags = flags; h.blast = blast; h.head = (flags & 1) !== 0;
        if (by === me) this.myHit(flags, h.dmgWin / hull, x, y, z);
        break;
      }
      case Ev.OrdSpawn: {
        // a teammate's rocket leaves the tube (yours already got its launch on the trigger frame)
        const [, kind, , , x, y, z, vx, vy, vz, owner] = f;
        if (owner === me || (kind !== OKind.Rocket && kind !== OKind.Harpoon)) break;
        const l = Math.hypot(vx, vy, vz) || 1;
        this.fxx.launch(kind === OKind.Rocket ? 1 : 0.5, x, y, z, vx / l, vy / l, vz / l, false);
        break;
      }
      case Ev.EnemyDie: {
        const [eid, type, x, y, z, killer] = f;
        this.kills.push({ id: eid, type, x, y, z, killer });
        break;
      }
      case Ev.Explosion: {
        const [x, y, z, r] = f;
        this.blast(x, y, z, r, false);
        break;
      }
      case Ev.Detonate: {
        const [, pid, , x, y, z, r] = f;
        this.blast(x, y, z, Math.max(3, r), pid === me);
        break;
      }
    }
  }

  private nearBlast(x: number, y: number, z: number): Blast | null {
    for (let i = this.blasts.length - 1; i >= 0; i--) {
      const b = this.blasts[i];
      if (this.time - b.at > 0.15) break;
      if ((x - b.x) ** 2 + (y - b.y) ** 2 + (z - b.z) ** 2 < (b.r * 1.3) ** 2) return b;
    }
    return null;
  }

  private myHit(flags: number, frac: number, x: number, y: number, z: number) {
    const kind = flags & 4 ? 2 : flags & 1 ? 1 : flags & 2 ? 3 : 0;
    // a crit reads as a crit even through a shield; only armour (no damage through) overrides it
    this.hitKind = kind; this.hitPow = Math.min(1.5, 0.3 + frac * 3);
    if (kind === 1) {
      // a crit lands with a lens tap and a tick of rumble, the pad's "ding"
      this.rig.punch(0, 0, 0, -0.5 * this.shakeScale, 30);
      this.input.rumble(0.05, 0.6, 45);
    }
    const pos = { x, y, z };
    if (flags & 8) return; // detonation numbers ride the detonate event
    if (kind === 2) this.play('impact_armor', pos, 0.7, 0.95 + Math.random() * 0.1, 'armor');
    else if (kind === 3) this.play('impact_shield', pos, 0.6, 1, 'shield_hit');
    else this.play('impact_metal', pos, 0.45, 0.9 + Math.random() * 0.2);
    // the confirm lives in the ear: crits ring, body hits tick, pitch climbing on a fast string
    if (kind === 1) this.play('headshot', undefined, 0.75, 1);
    else if (kind === 0) this.play('hit', undefined, 0.5, 1 + Math.min(0.25, frac));
  }

  private blast(x: number, y: number, z: number, r: number, mine: boolean) {
    this.blasts.push({ x, y, z, r, at: this.time });
    if (this.blasts.length > 16) this.blasts.shift();
    const cam = this.rig.cam.position;
    const d = Math.hypot(cam.x - x, cam.y - y, cam.z - z);
    const near = Math.max(0, 1 - d / Math.max(30, r * 8));
    if (near <= 0 && !mine) return;
    const k = Math.max(near, mine ? 0.6 : 0) * Math.min(1.6, 0.5 + r / 6);
    const s = this.shakeScale;
    // the blast shoves the lens away from it (up and to the side), and the fov breathes out
    const sideX = Math.cos(this.input.yaw), sideZ = -Math.sin(this.input.yaw);
    const side = ((x - cam.x) * sideX + (z - cam.z) * sideZ) / Math.max(1, d);
    this.rig.punch(1.6 * DEG * k * s, 0, -side * 2.2 * DEG * k * s, 2.5 * k * s, 13);
    this.input.rumble(Math.min(1, 0.5 + k * 0.5), 0.35 * k, 140 + 160 * k);
    this.shocks.push({ x, y, z, at: this.time, r, k: Math.min(1, k) });
    if (this.shocks.length > 4) this.shocks.shift();
    if (k > 0.5) this.sfx.thump?.(Math.min(1, k));
    if (r >= 6) this.play(r >= 10 ? 'explosion_l' : 'explosion_m', { x, y, z }, 0.9, 1);
  }

  // ---------------------------------------------------------------- frame
  /** call after the session update, before the robots draw. Returns the FX clock rate (hit-stop). */
  update(dt: number) {
    this.time += dt;
    this.resolveKills();
    this.fxx.update(dt, this.time);
    this.pad.update();
    // later sounds (shells land a beat after the shot)
    for (let i = this.later.length - 1; i >= 0; i--) {
      const l = this.later[i];
      if (this.time >= l.at) { this.sfx.play(l.name, { x: l.x, y: l.y, z: l.z }, { volume: l.vol, pitch: l.pitch }); this.later.splice(i, 1); }
    }
    if (this.later.length > 64) this.later.splice(0, this.later.length - 64);
    // forget stale hit info
    if (this.hits.size > 96) for (const [id, h] of this.hits) if (this.time - h.at > HIT_KEEP) this.hits.delete(id);
    while (this.shocks.length && this.time - this.shocks[0].at > 0.6) this.shocks.shift();
    if (this.whizzed.size > 512) this.whizzed.clear();
    return this.time < this.stopUntil ? this.stopDepth : 1;
  }

  /** decorate a robot's render state with its last hit's direction and power */
  decorate(r: EnemyRS) {
    const h = this.hits.get(r.id);
    if (!h || this.time - h.at > HIT_KEEP) { r.hitPow = undefined; r.hitDull = undefined; return; }
    r.hitDX = h.dx; r.hitDZ = h.dz; r.hitPow = h.pow;
    r.hitDull = (h.flags & 4) !== 0 && (h.flags & 1) === 0 ? true : undefined;
  }

  /** a hurt robot smokes (call once per robot per frame) */
  damaged(e: NetEnemy) { this.fxx.damaged(e.id, e.type, e.x, e.y, e.z, e.hp); }

  /** enemy rounds passing close to your head crack past */
  whiz(id: number, x: number, y: number, z: number) {
    if (this.whizzed.has(id)) return;
    const c = this.rig.cam.position;
    if ((x - c.x) ** 2 + (y - c.y) ** 2 + (z - c.z) ** 2 < 2.2 * 2.2) { this.whizzed.add(id); this.play('bullet_whiz', { x, y, z }, 0.6, 0.9 + Math.random() * 0.2); }
  }

  private resolveKills() {
    const me = this.myId();
    for (const k of this.kills) {
      const h = this.hits.get(k.id);
      const def = ENEMIES[k.type] ?? ENEMIES[0];
      const recent = h && this.time - h.at < 0.25;
      const pow = recent ? h!.pow : 0.4, frac = recent ? h!.frac : 0;
      let kind = DeathKind.Topple;
      const heavy = k.type === EType.MetalBrute;
      if (recent && h!.blast && !heavy) kind = DeathKind.Blown;
      else if (recent && h!.head) kind = DeathKind.Headshot;
      else if (recent && frac >= 0.8 && !heavy) kind = DeathKind.Shatter; // one hit (or one shotgun blast) worth most of a hull
      else if (recent && frac >= 0.4 && def.weight <= 1.6) kind = DeathKind.Blown; // a big hit lifts the light ones off their feet
      const dx = h ? h.dx : 0, dz = h ? h.dz : 0;
      this.robots?.kill?.(k.id, kind, dx, dz, pow);
      this.fxx.kill(kind, k.type, k.x, k.y, k.z, dx, dz, pow);
      this.hits.delete(k.id);
      if (kind === DeathKind.Shatter || kind === DeathKind.Blown) this.play('debris_clatter', { x: k.x, y: k.y, z: k.z }, 0.6, 1);
      this.later.push({ at: this.time + 0.5 + Math.random() * 0.3, name: 'wreck_fizz', x: k.x, y: k.y, z: k.z, vol: 0.4, pitch: 0.9 + Math.random() * 0.2 });
      if (k.killer === me) this.myKill(k, kind, def.hp + def.shield);
    }
    this.kills.length = 0;
  }

  private myKill(k: Kill, kind: DeathKind, hull: number) {
    const s = this.shakeScale;
    const big = hull >= 120;
    this.killN = this.time - this.lastKillAt < STREAK ? this.killN + 1 : 1;
    this.lastKillAt = this.time;
    this.killHead = kind === DeathKind.Headshot;
    this.killBig = big;
    // hit-stop: the FX clock nearly freezes for a beat (the corpse holds its pose in the robot view)
    const ms = big ? 95 : this.killHead ? 70 : kind === DeathKind.Shatter ? 65 : 45;
    this.stopUntil = Math.max(this.stopUntil, this.time + ms / 1000);
    this.stopDepth = 0.2; // deep slow-mo, not a freeze: sparks still crawl
    // a punch IN (the lens leans toward the kill), heavier for big robots
    this.rig.punch(-0.3 * DEG * s, 0, 0, -(big ? 2.2 : this.killHead ? 1.4 : 0.9) * s, big ? 12 : 20);
    this.input.rumble(big ? 0.9 : 0.35, big ? 0.5 : 0.7, big ? 220 : 90);
    if (big) this.sfx.dip?.(0.35, 260);
    const pitch = 1 + Math.min(0.3, (this.killN - 1) * 0.06); // a multikill climbs
    const nm = this.killHead ? 'kill_head' : big ? 'kill_big' : 'kill';
    this.play(nm, undefined, 0.7, pitch, 'kill');
  }

  /** play a bank sound, or a fallback while the sound lane hasn't shipped it */
  private play(name: string, pos: { x: number; y: number; z: number } | undefined, vol: number, pitch: number, fallback?: string) {
    const n = BANK[name] ? name : fallback;
    if (n) this.sfx.play(n, pos, { volume: vol, pitch });
  }

  /** the death cry for a robot type: the sound lane's robot_die_<etype> once it lands, else null */
  dieSound(type: number): string | null {
    return BANK['robot_die_' + type] ? 'robot_die_' + type : null;
  }

  /** the shot sound for a weapon: the sound lane's shot_<id> once it lands, else the old map */
  shotSound(wid: number, old: [string, number, number]): [string, number, number] {
    return BANK['shot_' + wid] ? ['shot_' + wid, 1, 1] : old;
  }

  // ---------------------------------------------------------------- triggers + light bar
  /** each frame: the adaptive-trigger profile for what's in your hands */
  triggers(wid: number, canFire: boolean, canAim: boolean, rgb: [number, number, number], slot: number) {
    if (!this.pad.on) return;
    const w = WEAPONS[wid];
    const f = feelOf(wid);
    let r2 = TRIG.off();
    if (w && canFire) r2 = this.time < this.firingUntil && f.r2Fire ? f.r2Fire() : f.r2();
    // an empty gun's trigger goes slack: you feel the click of nothing
    const l2 = canAim && w && w.proj === Proj.Hitscan ? L2_AIM() : TRIG.off();
    this.pad.setTriggers(r2, l2);
    this.pad.light(rgb[0], rgb[1], rgb[2], slot);
  }

  // ---------------------------------------------------------------- hud
  hud(s: HudState) {
    s.hitKind = this.hitKind; s.hitPow = this.hitPow;
    s.killHead = this.killHead; s.killBig = this.killBig;
    s.killN = this.time - this.lastKillAt < STREAK + 0.5 ? this.killN : 0;
  }
}
